import React from "react"
import ScrollIntoView from "react-scroll-into-view"
import { theme } from "../../../utils/styles"

/**
 * Splash hero with optional background video or poster image + cinematic gradient overlay
 *
 * Props:
 * - title: string
 * - heroVideo: string (url)
 * - heroPoster: string (url)
 * - overlayOpacity: number (0..1), default 0.45 — controls how dark the bottom of the gradient gets
 * - align: "left" | "center" (default "left")
 */
const Splash = ({
  title,
  kicker,
  heroVideo,
  heroPoster,
  overlayOpacity = 0.45,
  align = "left",
}) => {
  // Ensure numeric opacity
  const op = typeof overlayOpacity === "number" ? overlayOpacity : 0.45
  const alphaHex = Math.round(Math.min(Math.max(op, 0), 1) * 255)
    .toString(16)
    .padStart(2, "0")

  const SAFE_VH_CSS = `
  @supports (height: 100svh) {
    .splash-hero { height: 100svh; }
  }
  @keyframes splash-fade-up {
    from { opacity: 0; transform: translateY(32px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .splash-title {
    animation: splash-fade-up 1s cubic-bezier(0.16, 1, 0.3, 1) both;
  }
  .splash-kicker, .splash-cta {
    animation: splash-fade-up 1s cubic-bezier(0.16, 1, 0.3, 1) both;
  }
  .splash-kicker { animation-delay: 0.05s; }
  .splash-cta { animation-delay: 0.2s; }
  .splash-cta:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.35);
  }
`

  const sectionStyle = {
    position: "relative",
    width: "100%",
    height: "100vh",
    minHeight: 560,
    overflow: "hidden",
    backgroundColor: theme.black,
    margin: 0,
    padding: 0,
  }

  const mediaStyle = {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  }

  // Full-height cinematic gradient: transparent at top, dark at bottom, so
  // text can sit confidently over the image instead of a flat band.
  const gradientOverlayStyle = {
    position: "absolute",
    inset: 0,
    background: `linear-gradient(180deg, ${theme.black}00 0%, ${theme.black}00 35%, ${theme.black}${alphaHex} 75%, ${theme.black}${alphaHex} 100%)`,
    pointerEvents: "none",
  }

  const contentBoxStyle = {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: align === "center" ? "center" : "flex-start",
    justifyContent: "flex-end",
    padding: "0 6vw 8vh",
    color: theme.white,
    zIndex: 2,
    textAlign: align === "center" ? "center" : "left",
  }

  const kickerStyle = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: theme.primary,
    marginBottom: 16,
  }

  const kickerDotStyle = {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: theme.primary,
    display: "inline-block",
    flexShrink: 0,
  }

  const titleStyle = {
    fontSize: "clamp(26px, 3.6vw, 44px)",
    lineHeight: 1.05,
    margin: 0,
    maxWidth: 960,
    fontWeight: 800,
    letterSpacing: "-0.02em",
  }

  const ctaButtonStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    marginTop: 28,
    padding: "14px 28px",
    background: theme.primary,
    color: theme.black,
    fontWeight: 700,
    fontSize: 15,
    borderRadius: 8,
    textDecoration: "none",
    fontFamily: "Nunito, sans-serif",
    cursor: "pointer",
    border: "none",
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
  }

  return (
    <section className="splash-hero" style={sectionStyle}>
      <style>{SAFE_VH_CSS}</style>
      {/* Background media */}
      {heroVideo ? (
        <video
          src={heroVideo}
          poster={heroPoster || undefined}
          autoPlay
          muted
          loop
          playsInline
          style={mediaStyle}
        />
      ) : heroPoster ? (
        <img src={heroPoster} alt="" style={mediaStyle} />
      ) : null}

      {/* Cinematic gradient overlay, full height */}
      <div style={gradientOverlayStyle} />

      {/* Foreground content */}
      <div style={contentBoxStyle}>
        {kicker ? (
          <div className="splash-kicker" style={kickerStyle}>
            <span style={kickerDotStyle} />
            {kicker}
          </div>
        ) : null}
        {title ? (
          <h1 className="splash-title" style={titleStyle}>
            {title}
          </h1>
        ) : null}
        <ScrollIntoView selector="#technology">
          <button className="splash-cta" style={ctaButtonStyle}>
            Explore the technology
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M5 12l7 7 7-7" />
            </svg>
          </button>
        </ScrollIntoView>
      </div>
    </section>
  )
}

export default Splash
